import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ArrowLeft,
  Mail,
  Phone,
  Edit,
  Calendar,
  Percent,
  CreditCard,
  Plus,
  Trash2,
  FilePlus,
  Activity,
  Store,
  Link,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  Unlink,
  Check,
  X,
  Download,
  Upload,
  Globe,
  MapPin,
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/ui/data-table";
import { CountrySelect } from "@/components/ui/country-select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DelinkConfirmationDialog } from "@/components/program/DelinkConfirmationDialog";
import { ContactDeleteConfirmationDialog } from "@/components/program/ContactDeleteConfirmationDialog";
import { APIClient, ClientApi, ProgramApi } from "@/api";
import { DocumentApi, DocumentUploadResponse } from "@/api/document.api";
import {
  getDaysByFrequency,
  getFileUrl,
  getFrequencyByDays,
  isValidDate,
} from "@/utils";
import * as Yup from "yup";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_CURRENCY_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  PROGRAM_TYPES,
} from "@/constants/program.constant";
import countries from "@/constants/array/country_city.json";
import { MerchantApi } from "@/api/merchant.api";
import { LoaderButton } from "@/components/ui/loader-button";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { ActivityBlockchainDetails } from "@/components/common/ActivityBlockchainDetails";
import { useUserInfo } from "@/hooks/use-user-info";
import { isMerchantAdmin } from "@/utils/role.utils";

const contactSchema = Yup.object().shape({
  id: Yup.string().optional(),
  programId: Yup.string().required("ProgramId is required"),
  name: Yup.string()
    .required("Contact name is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  email: Yup.string()
    .required("Email is required")
    .email("Email must be a valid email address"),
  role: Yup.string()
    .required("Role is required")
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces are allowed"),
  phoneCountry: Yup.string().required("Country code is required"),
  phoneNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d+$/, "Phone must contain digits only")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number cannot exceed 15 digits"),
});

const documentUploadSchema = Yup.object().shape({
  documentName: Yup.string().required("Document name is required"),
  file: Yup.mixed().required("File is required"),
});

const editProgramSchema = Yup.object().shape({
  id: Yup.mixed().required(),
  vatNumber: Yup.string().optional().nullable(),
  country: Yup.string().required("Country is required"),
  clientId: Yup.string().required("Client is required"),
  city: Yup.string().required("City is required"),
  programType: Yup.string().required("Program Type is required"),
  programName: Yup.string()
    .required("Program Name is required")
    .matches(
      /^[a-zA-Z0-9\s\-':.]+$/,
      "Program name can only contain letters, numbers, spaces, and the following special characters: - ' : ."
    ),
  address: Yup.string().required("Program Location is required"),
  costPerPoint: Yup.number()
    .required("CPP is required")
    .min(0, "Cost per point cannot be negative")
    .typeError("Must be a number"),
  cashbackPercentage: Yup.number()
    .required("Percentage of cashback to be Accrued is required")
    .min(0, "Percentage cannot be negative")
    .max(100, "Percentage cannot be greater than 100")
    .typeError("Must be a number"),
  loyaltyPointsName: Yup.string().required("Loyalty Points Name is required"),
  // programCode is read-only / not edited in this sheet — keep optional so API quirks don't block save
  programCode: Yup.string().optional().nullable(),
  paymentCurrency: Yup.string().required("Payment Currency is required"),
  domain: Yup.string().required("Domain is required"),
  websiteUrl: Yup.string()
    .required("Website url is required")
    .test(
      "url",
      "Website URL must be a valid HTTP URL format",
      (value) => {
        if (!value?.trim()) return false;
        try {
          const normalized = value.trim().startsWith("http")
            ? value.trim()
            : `https://${value.trim()}`;
          new URL(normalized);
          return true;
        } catch {
          return false;
        }
      }
    ),
  invoiceFrequency: Yup.number()
    .required("Invoice frequency is required")
    .positive("Invoice frequency must be a positive number")
    .integer("Invoice frequency must be a whole number")
    .typeError("Must be a number"),
  txnCommissionPctChargedToPrg: Yup.number()
    .required("Transaction Commission is required")
    .min(0, "Transaction commission cannot be negative")
    .max(100, "Percentage cannot be greater than 100")
    .typeError("Must be a number"),
  offerDenomination: Yup.number()
    .required("Denomination is required")
    .integer("Value must be a whole number")
    .min(0, "Value cannot be negative")
    .typeError("Please enter a valid number"),
  legalCompanyName: Yup.string().optional().nullable(),
});

const ProgramDetails = ({
  basePath = "/accesspoint",
}: {
  basePath?: string;
}) => {
  // In a real app, we would fetch the program details based on the ID
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const returnToTab = (location.state as { returnToTab?: string } | null)
    ?.returnToTab;
  const returnToMerchant = (
    location.state as { returnToMerchant?: any } | null
  )?.returnToMerchant;
  const { userInfo } = useUserInfo();
  const merchantAdmin = isMerchantAdmin(userInfo?.roleCode);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [isAddMerchantDialogOpen, setIsAddMerchantDialogOpen] = useState(false);
  const [isEditProgramSheetOpen, setIsEditProgramSheetOpen] = useState(false);
  const [assignedMerchants, setAssignedMerchants] = useState([]);
  const [unassignedMerchants, setUnassignedMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsError, setMerchantsError] = useState(null);
  const [viewType, setViewType] = useState<"card" | "table">("card");
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLinkConfirmationOpen, setIsLinkConfirmationOpen] = useState(false);
  const [isDelinkConfirmationOpen, setIsDelinkConfirmationOpen] =
    useState(false);
  const [merchantToDelink, setMerchantToDelink] = useState<string>("");
  const [isContactDeleteConfirmationOpen, setIsContactDeleteConfirmationOpen] =
    useState(false);
  const [contactToDelete, setContactToDelete] = useState<any>(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [programDetails, setProgramDetails] = useState<any>({});
  const [programContacts, setProgramContacts] = useState<any>([]);
  const [phoneInputs, setPhoneInputs] = useState([
    { programContactId: "", phoneCountryCode: "", phoneNumber: "" },
  ]);
  const [preview, setPreview] = useState(null);
  const [clients, setClients] = useState([]);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    number | null
  >(null);
  const [uploadedDocument, setUploadedDocument] =
    useState<DocumentUploadResponse | null>(null);
  const [isDeleteDocumentOpen, setIsDeleteDocumentOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPage, setActivityPage] = useState(0);
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState(
    () => (location.state as { initialTab?: string } | null)?.initialTab || "details"
  );
  const [allMerchants, setAllMerchants] = useState([]);
  const [allMerchantsLoading, setAllMerchantsLoading] = useState(false);
  const [allMerchantsError, setAllMerchantsError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [domainVerificationStatus, setDomainVerificationStatus] = useState<
    "available" | "taken" | null
  >(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  async function getProgramDetailsById(programId) {
    try {
      const { data } = await ProgramApi.getProgramUsingId(programId);
      const program =
        data?.programVO ||
        (Array.isArray(data?.programVOs) ? data.programVOs[0] : null) ||
        (data?.id != null ? data : null);

      if (!program) {
        toast.error("Program not found");
        setProgramDetails({});
        return;
      }

      setProgramDetails(program);
    } catch (err) {
      toast.error("Failed to fetch program details");
      setProgramDetails({});
    }
  }

  const getProgramContactsByProgramId = async (id) => {
    await APIClient(`/program/getProgramContacts?programId=${id}`).then(
      (res) => {
        setProgramContacts(res.data);
        setPhoneInputs(
          res?.data?.map(
            ({ programContactPhoneVOList }) =>
              programContactPhoneVOList[0] || []
          )
        );
        // reset(generateDefaultValues(res.data));
      }
    );
  };

  const fetchDocumentsForProgram = async (programId: string) => {
    try {
      const { data } = await DocumentApi.getDocumentsForProgram(
        parseInt(programId)
      );
      if (data && Array.isArray(data)) {
        const formattedDocuments = data.map((doc: any) => ({
          id: doc.id,
          name: doc.originalFileName,
          uploadedBy: doc.uploadedBy,
          uploadedDate: doc.uploadDateTime,
          fileSize: doc.fileSize,
          size: `${(doc.fileSize / (1024 * 1024)).toFixed(2)} MB`,
          fileUrl: doc.fileUrl,
          documentName: doc.documentName,
          fileType: doc.fileType, // Add fileType field
        }));
        setDocuments(formattedDocuments);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      // Keep empty array if API fails
      setDocuments([]);
    }
  };

  async function fetchDataFromDb(id) {
    if (id) {
      getProgramDetailsById(id);
      getProgramContactsByProgramId(id);
      fetchDocumentsForProgram(id);
    }
  }
  useEffect(() => {
    fetchDataFromDb(id);
    getClients();
  }, [id]);

  const getClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      setClients(
        (res?.data || [])?.map((client) => ({
          value: `${client?.id || ""}`,
          label: client?.clientName,
        }))
      );
    } catch (error) {
      toast.error("Failed to fetch clients");
    }
  };

  function generateDefaultValues(data) {
    const country = (countries as any)?.find(
      (c) =>
        c?.name?.toLowerCase() === String(data?.country || "").toLowerCase()
    );
    const parseOptionalNumber = (value: unknown) => {
      if (value === null || value === undefined || value === "") return null;
      const parsed = parseFloat(String(value).replace(/[$%]/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      id: data?.id,
      vatNumber: data?.taxId || "",
      country: country ? JSON.stringify(country) : "",
      clientId: `${data?.clientId || ""}`,
      city: data?.city || "",
      programType: data?.programType || "",
      programName: data?.programName || "",
      address: data?.address || "",
      costPerPoint: parseOptionalNumber(data?.costPerPoint),
      cashbackPercentage: parseOptionalNumber(
        data?.percentageOfCashbackToBeAccrued
      ),
      loyaltyPointsName: data?.loyaltyCurrencyId || "",
      programCode: data?.programCode || "",
      paymentCurrency: data?.currencyVO?.id ? String(data?.currencyVO?.id) : "",
      domain: data?.domain || "",
      websiteUrl: data?.websiteUrl || "",
      invoiceFrequency:
        data?.invoiceFrequencyInDays != null &&
        data?.invoiceFrequencyInDays !== ""
          ? Number(data.invoiceFrequencyInDays)
          : null,
      txnCommissionPctChargedToPrg: parseOptionalNumber(
        data?.txnCommissionPctChargedToPrg
      ),
      offerDenomination: parseOptionalNumber(data?.offerDenomination),
      legalCompanyName: data?.clientVO?.clientName || "",
    };
  }

  const defaultValues = useMemo(
    () => generateDefaultValues(programDetails),
    [programDetails]
  );
  // Form for editing program details
  const programForm = useForm({
    resolver: yupResolver(editProgramSchema) as any,
    defaultValues,
  }) as any;

  const {
    reset,
    formState: { errors },
  } = programForm;

  // Function to check if a tab has validation errors
  const getTabErrors = () => {
    const tabErrors = {
      general: false,
      loyalty: false,
      payment: false,
      address: false,
    };

    // General tab errors
    if (
      errors.programName ||
      errors.programType ||
      errors.websiteUrl ||
      errors.domain ||
      errors.clientId ||
      errors.vatNumber ||
      errors.legalCompanyName
    ) {
      tabErrors.general = true;
    }

    // Loyalty tab errors
    if (
      errors.costPerPoint ||
      errors.cashbackPercentage ||
      errors.loyaltyPointsName
    ) {
      tabErrors.loyalty = true;
    }

    // Payment tab errors
    if (
      errors.paymentCurrency ||
      errors.invoiceFrequency ||
      errors.txnCommissionPctChargedToPrg ||
      errors.offerDenomination
    ) {
      tabErrors.payment = true;
    }

    // Address tab errors
    if (errors.country || errors.city || errors.address) {
      tabErrors.address = true;
    }

    return tabErrors;
  };

  const tabErrors = getTabErrors();

  useEffect(() => {
    if (programDetails) {
      const defaults = generateDefaultValues(programDetails);
      reset(defaults);

      // Set selectedCountry based on the program's country
      const country = (countries as any)?.find(
        (c) =>
          c?.name?.toLowerCase() ===
          String(programDetails?.country || "").toLowerCase()
      );
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [programDetails, reset]);
  // Form for adding new contact
  const contactForm = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      id: "",
      programId: id,
      phoneCountry: "",
      phoneNumber: "",
    },
  });

  // Form for adding new merchant - now just handles the submission
  const merchantForm = useForm({
    defaultValues: {
      selectedMerchants: [] as string[],
    },
  });

  // Form for document upload
  const documentUploadForm = useForm({
    resolver: yupResolver(documentUploadSchema),
    defaultValues: {
      documentName: "",
      file: null as File | null,
    },
  });

  const [addingContact, setAddingContact] = useState(false);

  const handleAddContact = async (data: any) => {
    setAddingContact(true);
    try {
      const contactData = {
        contactEmail: data?.email,
        contactName: data?.name,
        contactRole: data?.role,
        id: data?.id,
        programContactPhoneVOList: [
          {
            phoneNumber: data?.phoneNumber,
            phoneCountryCode: data?.phoneCountry,
            ...(data?.id && { programContactId: data?.id }),
          },
        ],
        programId: id,
      };
      const { data: responseData } = await addContact(contactData);
      setProgramContacts([...programContacts, responseData]);
      setIsAddContactDialogOpen(false);
      contactForm.reset();
      toast.success("Contact added successfully");
    } catch (err) {
      toast.error("Failed to add contact");
    } finally {
      setAddingContact(false);
    }
  };

  const handleAddMerchant = () => {
    if (selectedMerchants.length === 0) {
      toast.error("Please select at least one merchant");
      return;
    }

    setIsLinkConfirmationOpen(true);
  };

  const confirmLinkMerchants = async () => {
    try {
      const assignments = selectedMerchants.map((merchantId) => ({
        merchantId,
        programId: Number(id),
        isActive: true,
        merchantProgramCppVO: { cpp: 0.34 },
      }));
      await MerchantApi.assignMerchantsToProgram(assignments);

      // Refresh the merchants data from the server to ensure consistency
      const { data } = await MerchantApi.getMerchantAssignmentDataFromDB({
        programId: Number(id),
        merchantType: "",
        country: "",
        merchantName: "",
      });

      setAssignedMerchants(data.assignedMerchants || []);
      setIsAddMerchantDialogOpen(false);
      setIsLinkConfirmationOpen(false);
      setSelectedMerchants([]);
      toast.success(`${assignments.length} merchant(s) linked successfully`);
    } catch (err) {
      toast.error("Failed to link merchants");
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setPreview(selectedFile);
    }
  };

  const handlePlusIconClick = () => {
    document.getElementById("fileInput").click();
  };

  const verifyDomain = async () => {
    const domain = programForm.getValues("domain");
    if (!domain || domain.trim() === "") {
      toast.error("Please enter a domain first");
      return;
    }

    const trimmedDomain = domain.trim();
    if (trimmedDomain === programDetails?.domain?.trim()) {
      setDomainVerificationStatus("available");
      toast.success("Domain is available");
      return;
    }

    setIsVerifyingDomain(true);
    setDomainVerificationStatus(null);

    try {
      const response = await ProgramApi.verifyProgramDetails({
        programCode: programDetails?.programCode?.trim() || "",
        domain: trimmedDomain,
      });

      const isDomainExist = response.data?.isDomainExist;

      if (isDomainExist === false) {
        setDomainVerificationStatus("available");
        toast.success("Domain is available");
      } else if (isDomainExist === true) {
        setDomainVerificationStatus("taken");
        toast.error("Domain already exists");
      } else {
        toast.error("Unexpected response from server");
        setDomainVerificationStatus(null);
      }
    } catch (error) {
      toast.error("Failed to verify domain");
      setDomainVerificationStatus(null);
    } finally {
      setIsVerifyingDomain(false);
    }
  };

  const handleUpdateProgram = async (formData) => {
    const trimmedDomain = formData?.domain?.trim();
    const originalDomain = programDetails?.domain?.trim();
    if (
      trimmedDomain !== originalDomain &&
      domainVerificationStatus !== "available"
    ) {
      toast.error(
        "Please verify that the domain is available before submitting"
      );
      return;
    }

    setIsUpdating(true);
    try {
      const { contacts, ...updatedData } = formData;
      const currency = PAYMENT_CURRENCY_OPTIONS?.find(
        (currency) => String(currency.id) === String(formData?.paymentCurrency)
      );
      const formDataToSend = new FormData();
      if (preview) {
        formDataToSend.append("programLogo", preview);
      }

      console.log(
        "formData?.clientId || programDetails?.clientId",
        formData?.clientId,
        programDetails?.clientId
      );
      const programData = {
        id: programDetails?.id,
        // ...updatedData,
        programName: formData?.programName,
        programType: formData?.programType,
        clientId: formData?.clientId,
        taxId: formData?.vatNumber,
        websiteUrl: formData?.websiteUrl,
        domain: formData?.domain,
        loyaltyCurrencyId: formData?.loyaltyPointsName,
        costPerPoint: formData?.costPerPoint,
        percentageOfCashbackToBeAccrued: formData?.cashbackPercentage,
        ...(currency && { currencyVO: { id: currency.id } }),
        invoiceFrequencyInDays: Number(formData?.invoiceFrequency), // Use user input directly
        txnCommissionPctChargedToPrg:
          formData?.txnCommissionPctChargedToPrg || "",
        offerDenomination: formData?.offerDenomination || "",
        address: formData?.address,
        country: selectedCountry?.name,
        city: formData?.city,
      };
      const blob = new Blob([JSON.stringify(programData)], {
        type: "application/json",
      });
      formDataToSend.append("programVO", blob);

      await APIClient("/program/createOrUpdateProgram", formDataToSend, {
        requestType: "post",
        contentType: "multipart/form-data",
      });
      fetchDataFromDb(id);

      // handleReset();
      toast.success("Updated successfully");
      setIsEditProgramSheetOpen(false);
      setDomainVerificationStatus(null);
      if (id) {
        navigate(`/accesspoint/programs/${id}`, { replace: true });
      }
    } catch (error) {
      toast.error("Failed to update program");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteContact = (contact: any) => {
    setContactToDelete(contact);
    setIsContactDeleteConfirmationOpen(true);
  };

  const confirmDeleteContact = async () => {
    try {
      await deleteContact(contactToDelete.id);
      setProgramContacts(
        programContacts.filter((contact) => contact.id !== contactToDelete.id)
      );
      toast.success("Contact deleted successfully");
    } catch (err) {
      toast.error("Failed to delete contact");
    }
  };

  const handleDelinkMerchant = (merchantId: string) => {
    setMerchantToDelink(merchantId);
    setIsDelinkConfirmationOpen(true);
  };

  const confirmDelinkMerchant = async () => {
    try {
      // Find the assignment for the merchant to delink
      const merchant = assignedMerchants.find((m) => m.id === merchantToDelink);
      if (!merchant) {
        throw new Error("Merchant not found");
      }

      if (!merchant.merchantProgramIdForSelectedProgram) {
        throw new Error("Merchant assignment ID not found");
      }

      await MerchantApi.unAssignMerchantsFromProgram([
        merchant.merchantProgramIdForSelectedProgram,
      ]);

      // Refresh the merchants data from the server to ensure consistency
      const { data } = await MerchantApi.getMerchantAssignmentDataFromDB({
        programId: Number(id),
        merchantType: "",
        country: "",
        merchantName: "",
      });

      setAssignedMerchants(data.assignedMerchants || []);
      setIsDelinkConfirmationOpen(false);
      setMerchantToDelink("");
      toast.success("Merchant delinked successfully");
    } catch (err) {
      toast.error("Failed to delink merchant");
    }
  };

  const addContact = (contact) => {
    return APIClient(
      "/program/createOrUpdateProgramContact",
      {
        programId: id,
        ...contact,
      },
      { requestType: "post" }
    );
  };

  const deleteContact = (programContactId) => {
    return APIClient(
      `/program/deleteProgramContact?programContactId=${programContactId}`
    );
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Open document upload modal
      documentUploadForm.setValue("file", file);
      setIsDocumentUploadOpen(true);
    }
  };

  const handleDocumentUpload = async (data: any) => {
    setIsDocumentUploading(true);
    try {
      const file = data.file;
      const documentName = data.documentName;

      const { data: response } = await DocumentApi.uploadDocumentForProgram(
        file,
        {
          programId: parseInt(id),
          documentName: documentName,
        }
      );

      // Add the uploaded document to the documents list
      const newDocument = {
        id: response.id,
        name: response.originalFileName,
        uploadedBy: response.uploadedBy,
        uploadedDate: response.uploadDateTime,
        fileSize: response.fileSize,
        size: `${(response.fileSize / (1024 * 1024)).toFixed(2)} MB`,
        fileUrl: response.fileUrl,
        documentName: response.documentName,
        fileType:
          response.fileType ||
          response.originalFileName?.split(".").pop() ||
          "",
      };

      setDocuments([...documents, newDocument]);
      setUploadedDocument(response);
      setIsDocumentUploadOpen(false);
      documentUploadForm.reset();
      toast.success(
        `Document "${response.originalFileName}" uploaded successfully`
      );
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setIsDocumentUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: any) => {
    setDownloadingDocumentId(doc.id);
    try {
      const response = await DocumentApi.downloadDocument(doc.id);
      const contentType =
        response.headers?.["content-type"] ||
        response.headers?.["Content-Type"] ||
        "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      const safeName = (doc.documentName || doc.name || "document").trim();
      a.download = safeName;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      toast.success(`Document "${doc.documentName}" downloaded successfully`);
    } catch (error) {
      toast.error("Failed to download document");
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleDeleteDocument = async (document: any) => {
    setDocumentToDelete(document);
    setIsDeleteDocumentOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      await DocumentApi.deleteDocument(documentToDelete.id);
      setDocuments(documents.filter((doc) => doc.id !== documentToDelete.id));
      toast.success(
        `Document "${documentToDelete.documentName}" deleted successfully`
      );
    } catch (error) {
      toast.error("Failed to delete document");
    } finally {
      setIsDeleteDocumentOpen(false);
      setDocumentToDelete(null);
    }
  };

  // Toggle merchant selection
  const toggleMerchantSelection = (merchantId: string) => {
    if (selectedMerchants.includes(merchantId)) {
      setSelectedMerchants(selectedMerchants.filter((id) => id !== merchantId));
    } else {
      setSelectedMerchants([...selectedMerchants, merchantId]);
    }
  };

  // Function to format date as relative time (e.g., "1 day ago")
  const formatRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const getFileIcon = (fileType: string) => {
    const lower = fileType?.toLowerCase() || "";

    // PDF Files - Red color with FileText icon
    if (lower.includes("pdf")) {
      return (
        <span className="mr-3 text-red-500">
          <FileText className="h-5 w-5" />
        </span>
      );
    }

    // Excel/Spreadsheet Files - Green color with FileSpreadsheet icon
    if (
      lower.includes("xls") ||
      lower.includes("xlsx") ||
      lower.includes("excel") ||
      lower.includes("spreadsheet") ||
      lower.includes("csv")
    ) {
      return (
        <span className="mr-3 text-green-600">
          <FileSpreadsheet className="h-5 w-5" />
        </span>
      );
    }

    // Word Documents - Blue color with FileText icon
    if (
      lower.includes("docx") ||
      lower.includes("word") ||
      lower.includes("doc")
    ) {
      return (
        <span className="mr-3 text-blue-600">
          <FileText className="h-5 w-5" />
        </span>
      );
    }

    // Image Files - Purple color with FileImage icon
    if (
      lower.includes("image") ||
      lower.includes("jpg") ||
      lower.includes("jpeg") ||
      lower.includes("png") ||
      lower.includes("gif") ||
      lower.includes("bmp") ||
      lower.includes("svg") ||
      lower.includes("webp")
    ) {
      return (
        <span className="mr-3 text-purple-600">
          <FileImage className="h-5 w-5" />
        </span>
      );
    }

    // Code Files - Orange color with FileCode icon
    if (
      lower.includes("code") ||
      lower.includes("javascript") ||
      lower.includes("typescript") ||
      lower.includes("html") ||
      lower.includes("css") ||
      lower.includes("php") ||
      lower.includes("python") ||
      lower.includes("java") ||
      lower.includes("json") ||
      lower.includes("xml")
    ) {
      return (
        <span className="mr-3 text-orange-600">
          <FileCode className="h-5 w-5" />
        </span>
      );
    }

    // Default for other files - Gray color with FileText icon
    return (
      <span className="mr-3 text-gray-500">
        <FileText className="h-5 w-5" />
      </span>
    );
  };

  // Filtered available merchants
  const filteredAvailableMerchants = allMerchants.filter(
    (merchant) =>
      !assignedMerchants.some((m) => m.id === merchant.id) &&
      (merchant.merchantName || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  // Merchant table columns
  const merchantColumns = [
    {
      header: "Merchant",
      cell: (row: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
            <img
              src={row.merchantImageUri}
              alt={row.merchantName}
              className="max-h-8 max-w-8"
              onError={handleImgError}
            />
          </div>
          <div>
            <h4
              className="text-lg font-semibold text-gray-800"
              title={row.merchantName}
            >
              {row.merchantName}
            </h4>
            {row.merchantCode && (
              <div className="text-xs text-gray-600 mb-1">
                Code: {row.merchantCode}
              </div>
            )}
            <p className="text-xs text-gray-500">{row.businessType} Merchant</p>
          </div>
        </div>
      ),
    },
    {
      header: "Category",
      cell: (row: any) => (
        <div className="space-y-1">
          {row.categoryVOList && row.categoryVOList.length > 0 ? (
            row.categoryVOList.map((cat: any) => (
              <div key={cat.id}>
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 border-blue-100 px-2 py-0.5 text-xs font-medium w-fit"
                >
                  {cat.categoryName}
                </Badge>
              </div>
            ))
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </div>
      ),
    },
    {
      header: "Cashback %",
      cell: (row: any) => (
        <div className="space-y-1">
          {row.categoryVOList && row.categoryVOList.length > 0 ? (
            row.categoryVOList.map((cat: any) => (
              <div key={cat.id}>
                <span className="text-sm text-gray-600">
                  {cat.cashbackPercentage != null
                    ? `${cat.cashbackPercentage}%`
                    : "-"}
                </span>
              </div>
            ))
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </div>
      ),
    },
    {
      header: "Status",
      cell: (row: any) => (
        <div
          className={`inline-flex px-2 py-1 rounded-full text-xs ${
            row.isActive
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.isActive ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      header: "Actions",
      cell: (row: any) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() =>
              navigate(`${basePath}/merchants/${row.id}`, {
                state: {
                  merchant: row,
                  returnTo: `${basePath}/programs/${id}`,
                  returnToTab: "merchants",
                },
              })
            }
          >
            <Eye className="h-4 w-4 mr-1" /> View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 hover:text-red-600 font-bold text-base"
            onClick={() => handleDelinkMerchant(row.id)}
          >
            <Unlink className="h-4 w-4 mr-1" /> Delink
          </Button>
        </div>
      ),
    },
  ];

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const programCode = programDetails?.programCode;
      if (!programCode) {
        setActivityLogs([]);
        setActivityLoading(false);
        return;
      }
      const payload = {
        id: null,
        programCode,
        merchantCode: null,
        pageNumber: activityPage,
        pageSize: activityPageSize,
        sortOrder: "desc",
        sortBy: "createdAt",
      };
      const { data } = await ProgramApi.getActivityLogs(payload);
      setActivityLogs(data.activityLogVOs || []);
      setActivityTotalRecords(data.totalRecords || 0);
    } catch (err) {
      setActivityError("Failed to fetch activity logs");
      toast.error("Failed to fetch activity logs");
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    const tab = (location.state as { initialTab?: string } | null)?.initialTab;
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.state]);

  useEffect(() => {
    if (activeTab === "activity" && programDetails?.programCode) {
      fetchActivityLogs();
    }
  }, [activeTab, programDetails?.programCode, activityPage, activityPageSize]);

  useEffect(() => {
    if (!id) return;
    setMerchantsLoading(true);
    setMerchantsError(null);
    MerchantApi.getMerchantAssignmentDataFromDB({
      programId: Number(id),
      merchantType: "",
      country: "",
      merchantName: "",
    })
      .then(({ data }) => {
        const assigned = Array.isArray(data?.assignedMerchants)
          ? [...data.assignedMerchants]
          : [];
        // Sort by merchantRank ascending; if missing, push to end (no normalization)
        assigned.sort((a, b) => {
          const ar =
            typeof a.merchantRank === "number"
              ? a.merchantRank
              : Number.MAX_SAFE_INTEGER;
          const br =
            typeof b.merchantRank === "number"
              ? b.merchantRank
              : Number.MAX_SAFE_INTEGER;
          return ar - br;
        });
        setAssignedMerchants(assigned);
        setUnassignedMerchants(data.unassignedMerchants || []);
      })
      .catch((err) => {
        setMerchantsError("Failed to fetch merchants");
        toast.error("Failed to fetch merchants");
      })
      .finally(() => setMerchantsLoading(false));
  }, [id]);

  // Sorted list memo (already normalized, but keep stable reference)
  const sortedAssignedMerchants = useMemo(() => {
    const list = Array.isArray(assignedMerchants) ? [...assignedMerchants] : [];
    list.sort((a, b) => (a.merchantRank ?? 0) - (b.merchantRank ?? 0));
    return list;
  }, [assignedMerchants]);

  // Rank change: call API and refetch from server; no local reordering
  const handleChangeMerchantRank = async (
    merchantId: any,
    newRankValue: string
  ) => {
    const newRank = parseInt(newRankValue, 10);
    if (!Number.isFinite(newRank) || newRank < 1) return;
    try {
      const merchant: any = assignedMerchants.find(
        (m: any) => m.id === merchantId
      );
      const programCode = programDetails?.programCode;
      const merchantCode = merchant?.merchantCode;
      if (!programCode || !merchantCode) {
        toast.error("Missing program or merchant code");
        return;
      }

      const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
      const res = await fetch(
        `${API_BASE}/merchant/assign-rank/${encodeURIComponent(
          programCode
        )}/${encodeURIComponent(merchantCode)}/${encodeURIComponent(
          String(newRank)
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "Failed to assign rank");
      }

      // Refetch assignments to reflect latest ordering
      const { data } = await MerchantApi.getMerchantAssignmentDataFromDB({
        programId: Number(id),
        merchantType: "",
        country: "",
        merchantName: "",
      });
      const assigned = Array.isArray(data?.assignedMerchants)
        ? [...data.assignedMerchants]
        : [];
      assigned.sort((a, b) => {
        const ar =
          typeof a.merchantRank === "number"
            ? a.merchantRank
            : Number.MAX_SAFE_INTEGER;
        const br =
          typeof b.merchantRank === "number"
            ? b.merchantRank
            : Number.MAX_SAFE_INTEGER;
        return ar - br;
      });
      setAssignedMerchants(assigned);
      setUnassignedMerchants(data.unassignedMerchants || []);
      toast.success("Merchant rank updated");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update merchant rank");
    }
  };

  useEffect(() => {
    if (isAddMerchantDialogOpen) {
      setAllMerchantsLoading(true);
      setAllMerchantsError(null);
      MerchantApi.getAllMerchants()
        .then(({ data }) => {
          setAllMerchants(data || []);
        })
        .catch((err) => {
          setAllMerchantsError("Failed to fetch all merchants");
          toast.error("Failed to fetch all merchants");
        })
        .finally(() => setAllMerchantsLoading(false));
    }
  }, [isAddMerchantDialogOpen]);

  // Helper for fallback image
  const handleImgError = (e) => {
    e.currentTarget.src = "/placeholder.svg";
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          className="bg-white"
          onClick={() => {
            if (returnTo) {
              navigate(returnTo, {
                state: {
                  ...(returnToMerchant ? { merchant: returnToMerchant } : {}),
                  ...(returnToTab ? { initialTab: returnToTab } : {}),
                },
              });
            } else {
              navigate(`${basePath}/programs`);
            }
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Program Details</h1>
          <p className="text-gray-500">View and manage program information</p>
        </div>
      </div>

      {/* Program header card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={getFileUrl(programDetails?.programLogoUri)}
                  alt={programDetails?.programName}
                  className="max-h-16 max-w-16"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {programDetails?.programName}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-gray-500">
                  <span className="text-sm">
                    Code: {programDetails?.programCode}
                  </span>
                  {isValidDate(programDetails?.createdDateInMillis) && (
                    <span className="text-sm flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                      {formatRelativeTime(
                        new Date(programDetails?.createdDateInMillis) as any
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Sheet
              open={isEditProgramSheetOpen}
              onOpenChange={(open) => {
                setIsEditProgramSheetOpen(open);
                if (open) {
                  setDomainVerificationStatus("available");
                } else {
                  setDomainVerificationStatus(null);
                }
              }}
            >
              <PermissionGuard permissionCode={PERMISSION_CODES.UPDATE_PROGRAM}>
                <SheetTrigger asChild>
                  <Button>
                    <Edit className="h-4 w-4 mr-2" /> Edit Program
                  </Button>
                </SheetTrigger>
              </PermissionGuard>
              <SheetContent className="sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Edit Program</SheetTitle>
                  <SheetDescription>
                    Make changes to your program here. Click save when you're
                    done.
                  </SheetDescription>
                </SheetHeader>
                <div className="py-4">
                  <Tabs defaultValue="general" className="w-full">
                    <TabsList className="grid grid-cols-4 mb-4">
                      <TabsTrigger
                        value="general"
                        className={
                          tabErrors.general
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        General
                      </TabsTrigger>
                      <TabsTrigger
                        value="loyalty"
                        className={
                          tabErrors.loyalty
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Loyalty
                      </TabsTrigger>
                      <TabsTrigger
                        value="payment"
                        className={
                          tabErrors.payment
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Payment
                      </TabsTrigger>
                      <TabsTrigger
                        value="address"
                        className={
                          tabErrors.address
                            ? "bg-red-100 text-red-800 border-red-300"
                            : ""
                        }
                      >
                        Address
                      </TabsTrigger>
                    </TabsList>

                    <Form {...programForm}>
                      <form
                        onSubmit={programForm.handleSubmit(
                          handleUpdateProgram,
                          (validationErrors) => {
                            const firstError = Object.values(
                              validationErrors || {}
                            ).find(
                              (err: any) => err?.message
                            ) as { message?: string } | undefined;
                            toast.error(
                              firstError?.message ||
                                "Please fix the highlighted validation errors"
                            );
                          }
                        )}
                      >
                        <input
                          type="file"
                          id="fileInput"
                          name="programLogo"
                          style={{ display: "none", cursor: "pointer" }}
                          onChange={handleFileChange}
                        />
                        <TabsContent value="general" className="space-y-4 mt-4">
                          <div
                            className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200"
                            onClick={handlePlusIconClick}
                          >
                            <img
                              src={
                                preview
                                  ? URL.createObjectURL(preview)
                                  : getFileUrl(programDetails?.programLogoUri)
                              }
                              alt={programDetails?.programName}
                              className="max-h-16 max-w-16 cursor-pointer"
                            />
                          </div>
                          <FormField
                            control={programForm.control}
                            name="programName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Program Name*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^a-zA-Z0-9\s\-':.]/g,
                                        ""
                                      );
                                      field.onChange(value);
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="programType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Program Type*
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select program type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {PROGRAM_TYPE_OPTIONS?.map((program) => (
                                      <SelectItem
                                        key={program?.id}
                                        value={program?.value}
                                      >
                                        {program?.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {/* Conditionally render Legal Company Name for non-AccessPoint */}
                          {/* {programForm.watch("programType") !==
                            PROGRAM_TYPES.EARN && (
                            <FormField
                              control={programForm.control}
                              name="legalCompanyName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="font-semibold text-gray-700 text-base">Legal Company Name</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          )} */}

                          <FormField
                            control={programForm.control}
                            name="clientId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Client*
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  disabled={true}
                                >
                                  <FormControl>
                                    <SelectTrigger className="bg-gray-100">
                                      <SelectValue placeholder="Select Client" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {clients?.map((client) => (
                                      <SelectItem
                                        key={client?.value}
                                        value={client?.value}
                                      >
                                        {client?.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="vatNumber"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  VAT Number
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="websiteUrl"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Website URL*
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="domain"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Domain name*
                                </FormLabel>
                                <FormControl>
                                  <div className="space-y-2">
                                    <div className="flex gap-2">
                                      <Input
                                        placeholder="e.g. example"
                                        {...field}
                                        onChange={(e) => {
                                          field.onChange(e);
                                          const newDomain = e.target.value.trim();
                                          const originalDomain =
                                            programDetails?.domain?.trim();
                                          if (newDomain === originalDomain) {
                                            setDomainVerificationStatus(
                                              "available"
                                            );
                                          } else {
                                            setDomainVerificationStatus(null);
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={verifyDomain}
                                        disabled={
                                          isVerifyingDomain || !field.value
                                        }
                                        className="shrink-0"
                                      >
                                        {isVerifyingDomain ? (
                                          <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Verifying...
                                          </>
                                        ) : (
                                          "Verify"
                                        )}
                                      </Button>
                                    </div>
                                    {domainVerificationStatus && (
                                      <div className="flex items-center gap-2 text-sm">
                                        {domainVerificationStatus ===
                                        "available" ? (
                                          <>
                                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                                            <span className="text-green-600">
                                              Domain is available
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <XCircle className="h-4 w-4 text-red-600" />
                                            <span className="text-red-600">
                                              Domain already exists
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                    {domainVerificationStatus !==
                                      "available" && (
                                      <p className="text-sm text-gray-500">
                                        Domain must be verified as available
                                        before you can submit the form.
                                      </p>
                                    )}
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <TabsContent value="loyalty" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="loyaltyPointsName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Loyalty Points Name*
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="costPerPoint"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Cost Per Point ($)*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.001"
                                    min={0}
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="cashbackPercentage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Percentage of cashback to be Accrued (%)*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <TabsContent value="payment" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="paymentCurrency"
                            defaultValue={
                              programDetails?.currencyVO?.id
                                ? String(programDetails?.currencyVO?.id)
                                : ""
                            }
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Payment Currency*
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value || undefined}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {PAYMENT_CURRENCY_OPTIONS.map(
                                      (currency) => (
                                        <SelectItem
                                          key={currency.id}
                                          value={String(currency.id)}
                                        >{`${currency.value} - ${currency.label}`}</SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="invoiceFrequency"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Invoice Frequency (days)*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    placeholder="e.g. 30"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="offerDenomination"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Denomination*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="e.g. 10"
                                    {...field}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      field.onChange(parseFloat(value) || null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "-" ||
                                        e.key === "+" ||
                                        e.key === "e" ||
                                        e.key === "E"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onWheel={(e) => {
                                      e.currentTarget.blur();
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={programForm.control}
                            name="txnCommissionPctChargedToPrg"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Transaction Commission*
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    placeholder="e.g. 12"
                                    {...field}
                                    onChange={(e) => {
                                      let value = e.target.value.replace(
                                        /[^0-9.]/g,
                                        ""
                                      );
                                      const parts = value.split(".");
                                      if (parts.length > 2) {
                                        value =
                                          parts[0] +
                                          "." +
                                          parts.slice(1).join("");
                                      }
                                      const num = parseFloat(value);
                                      if (!isNaN(num) && num > 100) {
                                        value = "100";
                                      }
                                      field.onChange(parseFloat(value) || null);
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                        {/* Address Tab */}
                        <TabsContent value="address" className="space-y-4 mt-4">
                          <FormField
                            control={programForm.control}
                            name="address"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Address*
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="country"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  Country*
                                </FormLabel>
                                <Select
                                  onValueChange={(value) => {
                                    const selected = JSON.parse(value);
                                    setSelectedCountry(selected);
                                    field.onChange(value);
                                    // Clear city when country changes
                                    programForm.setValue("city", "");
                                  }}
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select Country" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent
                                    onWheel={(e) => e.stopPropagation()}
                                  >
                                    <div className="max-h-[200px] overflow-y-auto">
                                      {(countries as any)?.map((country) => (
                                        <SelectItem
                                          key={country?.name}
                                          value={JSON.stringify(country)}
                                        >
                                          {country?.name}
                                        </SelectItem>
                                      ))}
                                    </div>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={programForm.control}
                            name="city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-semibold text-gray-700 text-base">
                                  City*
                                </FormLabel>
                                <VirtualizedCitySelect
                                  cities={selectedCountry?.cities || []}
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  placeholder="Select City"
                                  disabled={!selectedCountry?.cities?.length}
                                  // error={field.error?.message} // Removed, not present on field
                                />
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        <SheetFooter className="mt-6">
                          <SheetClose asChild>
                            <Button type="button" variant="outline">
                              Cancel
                            </Button>
                          </SheetClose>
                          <LoaderButton type="submit" loading={isUpdating}>
                            Save changes
                          </LoaderButton>
                        </SheetFooter>
                      </form>
                    </Form>
                  </Tabs>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Program Details
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger
            value="merchants"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Merchants
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Program Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Program Type
                    </p>
                    <p className="text-gray-800">
                      {programDetails.programType}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Client</p>
                    <p className="text-gray-800">
                      {programDetails.clientVO?.clientName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      VAT Number
                    </p>
                    <p className="text-gray-800">{programDetails.taxId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Website URL
                    </p>
                    <p className="text-gray-800">
                      {programDetails?.websiteUrl}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Domain name
                    </p>
                    <p className="text-gray-800">{programDetails?.domain}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loyalty Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Loyalty Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Loyalty Points Name
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails.loyaltyCurrencyId}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Cost Per Point
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails?.costPerPoint}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                      <Percent className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Percentage of cashback to be Accrued
                      </p>
                      <p className="text-gray-800 font-medium">
                        {programDetails?.percentageOfCashbackToBeAccrued}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Billing Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Payment Currency
                    </p>
                    <p className="text-gray-800">
                      {programDetails?.currencyVO?.iso3CurrencyCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Invoice Frequency
                    </p>
                    <p className="text-gray-800">
                      {programDetails?.invoiceFrequencyInDays}
                    </p>
                  </div>
                  {programDetails?.offerDenomination > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Denomination
                      </p>
                      <p className="text-gray-800">
                        {programDetails?.offerDenomination}
                      </p>
                    </div>
                  )}
                  {programDetails?.txnCommissionPctChargedToPrg ? (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Transaction Commission
                      </p>
                      <p className="text-gray-800">
                        {programDetails?.txnCommissionPctChargedToPrg}
                      </p>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            {/* Program Address */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Program Address
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Address</p>
                    <p className="text-gray-800">{programDetails.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">{programDetails?.city}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">{programDetails?.country}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Contacts
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddContactDialogOpen(true)}
                  disabled={merchantAdmin}
                >
                  <Plus className="h-4 w-4" /> Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {programContacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No contacts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Contacts Found</p>
                  <p className="mb-4">
                    No contacts are currently added to this program.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className=" gap-2"
                    onClick={() => setIsAddContactDialogOpen(true)}
                    disabled={merchantAdmin}
                  >
                    <Plus className="h-4 w-4" /> Create Your First Contact
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {programContacts.map((contact, index) => (
                    <div
                      key={contact?.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 sm:mb-0">
                        <p className="font-medium text-gray-800">
                          {contact?.contactName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {contact?.contactRole}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center text-gray-500">
                          <Mail className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            {contact?.contactEmail}
                          </span>
                        </div>
                        {contact?.programContactPhoneVOList?.[0] && (
                          <div className="flex items-center text-gray-500">
                            <Phone className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              {contact?.programContactPhoneVOList?.[0]
                                .phoneCountryCode && (
                                <span className="text-gray-400">
                                  {
                                    contact?.programContactPhoneVOList?.[0]
                                      .phoneCountryCode
                                  }{" "}
                                </span>
                              )}
                              {
                                contact?.programContactPhoneVOList?.[0]
                                  .phoneNumber
                              }
                            </span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-red-600 p-1 h-auto"
                          onClick={() => handleDeleteContact(contact)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Documents
              </CardTitle>
              <div>
                <Button
                  size="sm"
                  className="relative"
                  onClick={() => setIsDocumentUploadOpen(true)}
                >
                  <FilePlus className="h-4 w-4 mr-2" /> Upload Document
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No documents available. Upload your first document.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="py-4 flex flex-col sm:flex-row sm:items-center justify-between first:pt-0 last:pb-0"
                    >
                      <div className="flex items-center">
                        {getFileIcon(doc.fileType)}
                        <div>
                          <p
                            className="font-medium text-gray-800 truncate max-w-xs"
                            title={doc.documentName}
                          >
                            {doc.documentName}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded by {doc.uploadedBy} •
                            {doc.fileSize >= 10240
                              ? `${(doc.fileSize / (1024 * 1024)).toFixed(
                                  2
                                )} MB`
                              : `${doc.fileSize} bytes`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mt-2 sm:mt-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          disabled={downloadingDocumentId === doc.id}
                        >
                          {downloadingDocumentId === doc.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 ml-2"
                          onClick={() => handleDeleteDocument(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Merchants Tab */}
        <TabsContent value="merchants" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Program Merchants
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Table View</span>
                  <Switch
                    checked={viewType === "card"}
                    onCheckedChange={(checked) =>
                      setViewType(checked ? "card" : "table")
                    }
                  />
                  <span className="text-sm text-gray-600">Card View</span>
                </div>
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddMerchantDialogOpen(true)}
                  disabled={merchantAdmin}
                >
                  <Link className="h-4 w-4" /> Link Merchants
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {assignedMerchants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No merchants"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Merchants Found</p>
                  <p className="mb-4">
                    No merchants are currently linked to this program.
                  </p>
                </div>
              ) : viewType === "card" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedAssignedMerchants.map((merchant) => (
                    <Card
                      key={merchant.id}
                      className="border-0 shadow-md rounded-xl bg-gradient-to-br from-white to-gray-50 hover:shadow-lg transition-shadow"
                    >
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center shadow-sm border">
                            <img
                              src={merchant.merchantImageUri}
                              alt={merchant.merchantName}
                              className="max-h-14 max-w-14 object-contain"
                              onError={handleImgError}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4
                              className="text-xl font-bold text-gray-900 mb-1"
                              title={merchant.merchantName}
                            >
                              {merchant.merchantName}
                            </h4>
                            {merchant.merchantCode && (
                              <div className="text-xs text-gray-500 mb-1">
                                Code:{" "}
                                <span className="font-semibold">
                                  {merchant.merchantCode}
                                </span>
                              </div>
                            )}

                            {merchant.categoryVOList &&
                              merchant.categoryVOList.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {merchant.categoryVOList.map((cat) => (
                                    <Badge
                                      key={cat.id}
                                      variant="secondary"
                                      className="bg-blue-50 text-blue-700 border-blue-100 px-2 py-0.5 text-xs font-medium"
                                    >
                                      {cat.categoryName}
                                      {cat.cashbackPercentage != null
                                        ? ` (${cat.cashbackPercentage}% cashback)`
                                        : ""}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            <div className="flex flex-wrap gap-2 items-center mt-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  merchant.isActiveForSelectedProgram
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {merchant.isActiveForSelectedProgram
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-semibold">
                                {merchant.businessType} Merchant
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col gap-2 text-sm mt-2">
                          {(merchant.address ||
                            merchant.city ||
                            merchant.country) && (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <span>
                                {[
                                  merchant.address,
                                  merchant.city,
                                  merchant.country,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                          {merchant.email && (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span>{merchant.email}</span>
                            </div>
                          )}
                          {merchant.phone && (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{merchant.phone}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-6 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500">
                              Rank
                            </label>
                            <Select
                              value={String(merchant.merchantRank ?? "")}
                              onValueChange={(val) =>
                                handleChangeMerchantRank(merchant.id, val)
                              }
                              disabled={merchantAdmin}
                            >
                              <SelectTrigger className="w-24 h-8 text-sm">
                                <SelectValue placeholder="Rank" />
                              </SelectTrigger>
                              <SelectContent className="max-h-64">
                                {Array.from({
                                  length: sortedAssignedMerchants.length,
                                }).map((_, i) => (
                                  <SelectItem key={i + 1} value={String(i + 1)}>
                                    {i + 1}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() =>
                                navigate(
                                  `${basePath}/merchants/${merchant.id}`,
                                  {
                                    state: {
                                      merchant,
                                      returnTo: `${basePath}/programs/${id}`,
                                      returnToTab: "merchants",
                                    },
                                  }
                                )
                              }
                            >
                              <Eye className="h-4 w-4 mr-1" /> View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-red-600 font-bold text-base"
                              onClick={() =>
                                handleDelinkMerchant(merchant.id)
                              }
                            >
                              <Unlink className="h-4 w-4 mr-1" /> Delink
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <DataTable
                  title=""
                  data={sortedAssignedMerchants}
                  columns={[
                    {
                      header: "Rank",
                      cell: (row: any) => (
                        <div className="min-w-[90px]">
                          <Select
                            value={String(row.merchantRank ?? "")}
                            onValueChange={(val) =>
                              handleChangeMerchantRank(row.id, val)
                            }
                            disabled={merchantAdmin}
                          >
                            <SelectTrigger className="w-20 h-8 text-xs">
                              <SelectValue placeholder="Rank" />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                              {Array.from({
                                length: sortedAssignedMerchants.length,
                              }).map((_, i) => (
                                <SelectItem key={i + 1} value={String(i + 1)}>
                                  {i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ),
                    },
                    ...merchantColumns,
                  ]}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {activityLoading ? (
                <div className="text-center text-gray-500">
                  Loading activity...
                </div>
              ) : activityError ? (
                <div className="text-center text-red-500">{activityError}</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center text-gray-500">
                  No activity found.
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-100">
                    {activityLogs.map((log) => (
                      <div key={log.id} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-md bg-blue-50 text-blue-600 mt-1">
                            <Activity className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Event Name (Action) */}
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">
                                {log.action
                                  .replace(/_/g, " ")
                                  .toLowerCase()
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                            </div>
                            {/* Description */}
                            <div
                              className="text-gray-700 text-sm mb-1 truncate"
                              title={log.description}
                            >
                              {log.description}
                            </div>
                            {/* Meta: Who did what and when */}
                            <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500 mt-1">
                              <span
                                title={log.actorName}
                                className="font-medium text-gray-600"
                              >
                                {log.actorName}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span>
                                {log.createdAt
                                  ? formatRelativeTime(log.createdAt)
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <ActivityBlockchainDetails
                            blockchainDataVO={log.blockchainDataVO}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {!activityLoading && activityTotalRecords > 0 && (
                    <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Rows per page:</span>
                        <Select
                          value={activityPageSize.toString()}
                          onValueChange={(value) => {
                            setActivityPageSize(Number(value));
                            setActivityPage(0);
                          }}
                        >
                          <SelectTrigger className="w-[80px]">
                            <SelectValue placeholder={activityPageSize.toString()} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {Math.ceil(activityTotalRecords / activityPageSize) > 1 && (
                        <EnhancedPaginationControl
                          totalPages={Math.ceil(activityTotalRecords / activityPageSize)}
                          currentPage={activityPage}
                          onPageChange={(page) => setActivityPage(page)}
                        />
                      )}
                      <div className="w-[160px]" /> {/* Spacer to balance layout */}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for adding new contact */}
      <Dialog
        open={isAddContactDialogOpen}
        onOpenChange={setIsAddContactDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
          </DialogHeader>
          <Form {...contactForm}>
            <form
              onSubmit={contactForm.handleSubmit(handleAddContact)}
              className="space-y-4"
            >
              <FormField
                control={contactForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-700 text-base">
                      Name
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-700 text-base">
                      Email*
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={contactForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-700 text-base">
                      Role*
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Enter contact role" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <FormField
                  control={contactForm.control}
                  name="phoneCountry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-700 text-base">
                        Phone
                      </FormLabel>
                      <FormControl>
                        <CountrySelect
                          value={field?.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={contactForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="opacity-0">Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddContactDialogOpen(false)}
                >
                  Cancel
                </Button>
                <LoaderButton type="submit" loading={addingContact}>
                  <Plus className="h-4 w-4 mr-2" /> Add Contact
                </LoaderButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Dialog for linking new merchant - Updated with searchable dropdown */}
      <Dialog
        open={isAddMerchantDialogOpen}
        onOpenChange={setIsAddMerchantDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Merchants</DialogTitle>
            <DialogDescription>
              Select merchants to link to this program.
            </DialogDescription>
          </DialogHeader>

          <div className={`my-4 ${allMerchantsLoading ? "hidden" : ""}`}>
            <div className="mb-2">
              <p className="text-sm font-medium mb-1">Selected Merchants:</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedMerchants.length === 0 ? (
                  <p className="text-sm text-gray-500">No merchants selected</p>
                ) : (
                  selectedMerchants.map((merchantId) => {
                    const merchant = allMerchants.find(
                      (m) => m.id === merchantId
                    );
                    return merchant ? (
                      <Badge
                        key={merchantId}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {merchant.merchantName}
                        <button
                          onClick={() => toggleMerchantSelection(merchantId)}
                          className="ml-1 h-3 w-3 rounded-full bg-gray-400 text-white flex items-center justify-center"
                        >
                          <X className="h-2 w-2" />
                        </button>
                      </Badge>
                    ) : null;
                  })
                )}
              </div>
            </div>

            <div className="max-h-60 overflow-hidden">
              <Command className="rounded-lg border shadow-md">
                <CommandInput
                  placeholder="Search merchants..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList className="max-h-40 overflow-auto">
                  <CommandEmpty>No merchants found.</CommandEmpty>
                  <CommandGroup>
                    {filteredAvailableMerchants.length === 0 ? (
                      <div className="py-6 text-center text-sm">
                        No available merchants to link
                      </div>
                    ) : (
                      filteredAvailableMerchants.map((merchant) => (
                        <CommandItem
                          key={merchant.id}
                          onSelect={() => toggleMerchantSelection(merchant.id)}
                          className="flex items-center gap-2 py-3"
                        >
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                              selectedMerchants.includes(merchant.id)
                                ? "bg-primary border-primary"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedMerchants.includes(merchant.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-1">
                            <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                              <img
                                src={merchant.merchantImageUri}
                                alt={merchant.merchantName}
                                className="max-h-6 max-w-6"
                                onError={handleImgError}
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {merchant.merchantName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {merchant.businessType} Merchant
                              </p>
                            </div>
                          </div>
                        </CommandItem>
                      ))
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          </div>
          {allMerchantsLoading && (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading
              merchants...
            </div>
          )}

          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddMerchantDialogOpen(false);
                setSelectedMerchants([]);
                setSearchQuery("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMerchant}
              disabled={allMerchantsLoading || selectedMerchants.length === 0}
            >
              Link {selectedMerchants.length}{" "}
              {selectedMerchants.length === 1 ? "Merchant" : "Merchants"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Confirmation Dialog */}
      <Dialog
        open={isLinkConfirmationOpen}
        onOpenChange={setIsLinkConfirmationOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Merchant Linking</DialogTitle>
            <DialogDescription>
              Are you sure you want to link {selectedMerchants.length}{" "}
              {selectedMerchants.length === 1 ? "merchant" : "merchants"} to
              this program?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsLinkConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmLinkMerchants}>
              Link {selectedMerchants.length === 1 ? "Merchant" : "Merchants"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delink Confirmation Dialog */}
      <DelinkConfirmationDialog
        open={isDelinkConfirmationOpen}
        onOpenChange={setIsDelinkConfirmationOpen}
        onConfirm={confirmDelinkMerchant}
        itemName={
          assignedMerchants.find((m) => m.id === merchantToDelink)
            ?.merchantName || ""
        }
        itemType="merchant"
      />

      {/* Contact Delete Confirmation Dialog */}
      <ContactDeleteConfirmationDialog
        open={isContactDeleteConfirmationOpen}
        onOpenChange={setIsContactDeleteConfirmationOpen}
        onConfirm={confirmDeleteContact}
        contactName={contactToDelete?.contactName || ""}
      />

      {/* Document Upload Dialog */}
      <Dialog
        open={isDocumentUploadOpen}
        onOpenChange={setIsDocumentUploadOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for this program. Please provide a name for the
              document.
            </DialogDescription>
          </DialogHeader>
          <Form {...documentUploadForm}>
            <form
              onSubmit={documentUploadForm.handleSubmit(handleDocumentUpload)}
              className="space-y-4"
            >
              <FormField
                control={documentUploadForm.control}
                name="documentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-gray-700 text-base">
                      Document Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Service Agreement, Terms & Conditions"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={documentUploadForm.control}
                name="file"
                render={({ field: { value, onChange, ...fieldProps } }) => {
                  function truncateFileName(name, max = 32) {
                    if (name.length <= max) return name;
                    const extIndex = name.lastIndexOf(".");
                    const ext = extIndex !== -1 ? name.slice(extIndex) : "";
                    const base = name.slice(
                      0,
                      extIndex !== -1 ? extIndex : undefined
                    );
                    const keep = Math.max(8, max - ext.length - 5);
                    return base.slice(0, keep) + "..." + base.slice(-5) + ext;
                  }
                  return (
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-700 text-base">
                        File
                      </FormLabel>
                      <FormControl>
                        <div className="space-y-3">
                          {!value ? (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                              onClick={() =>
                                document
                                  .getElementById("document-file-input-program")
                                  ?.click()
                              }
                            >
                              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600 mb-1">
                                Click to select a file or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, DOC, DOCX, XLS, XLSX up to 10MB
                              </p>
                            </div>
                          ) : value instanceof File ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md min-w-0">
                              <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span
                                  className="text-sm text-gray-700 block truncate"
                                  title={value.name}
                                >
                                  {truncateFileName(value.name)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onChange(null);
                                  const fileInput = document.getElementById(
                                    "document-file-input-program"
                                  ) as HTMLInputElement;
                                  if (fileInput) {
                                    fileInput.value = "";
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : null}
                          <input
                            id="document-file-input-program"
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error("File size exceeds 10MB limit.");
                                  return;
                                }
                                onChange(file);
                              }
                            }}
                            {...fieldProps}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <DialogFooter className="sm:justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDocumentUploadOpen(false);
                    documentUploadForm.reset();
                  }}
                >
                  Cancel
                </Button>
                <LoaderButton
                  type="submit"
                  loading={isDocumentUploading}
                  disabled={
                    !documentUploadForm.watch("file") ||
                    !documentUploadForm.watch("documentName")
                  }
                >
                  <Upload className="h-4 w-4 mr-2" /> Upload Document
                </LoaderButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog
        open={isDeleteDocumentOpen}
        onOpenChange={setIsDeleteDocumentOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{documentToDelete?.documentName}
              "? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteDocumentOpen(false);
                setDocumentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteDocument}
            >
              Delete Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramDetails;
